import { useState, useCallback } from "react";
import {
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
} from "@stripe/react-stripe-js";
import type {
  StripeCardNumberElementChangeEvent,
  StripeCardExpiryElementChangeEvent,
  StripeCardCvcElementChangeEvent,
} from "@stripe/stripe-js";
import { useAnalytics } from "@/hooks/useAnalytics";

const commonStyle = {
  base: {
    fontSize: "14px",
    color: "#212121",
    "::placeholder": {
      color: "#D9D9D9",
    },
  },
  invalid: {
    color: "#ff4d4f",
  },
};

interface ElementOptions {
  placeholder: string;
  style: typeof commonStyle;
  autocomplete?: string;
  disableLink?: boolean;
}

const elementOptions = (
  placeholder: string,
  autocomplete?: string
): ElementOptions => {
  return {
    placeholder,
    style: commonStyle,
    disableLink: true, // Disable Stripe Link autofill
    ...(autocomplete ? { autocomplete } : {}),
  };
};

interface CreditCardCardFormProps {
  onElementChange: (
    event:
      | StripeCardNumberElementChangeEvent
      | StripeCardExpiryElementChangeEvent
      | StripeCardCvcElementChangeEvent
  ) => void;
  disabled?: boolean;
  onAnyFieldFocus?: () => void;
}

const CreditCardCardForm = ({
  onElementChange,
  disabled = false,
  onAnyFieldFocus,
}: CreditCardCardFormProps) => {
  const { track } = useAnalytics();
  
  // Track field validity state for blur events
  const [fieldValidity, setFieldValidity] = useState({
    cardNumber: false,
    expiry: false,
    cvc: false,
  });

  // Track blur events
  const handleFieldBlur = useCallback((fieldName: string, isValid: boolean) => {
    try {
      track('click_in_add_new_card', {
        field_name: fieldName,
        is_text_valid: isValid,
      });
    } catch {
      // Ignore tracking errors
    }
  }, [track]);

  const handleCardNumberChange = (
    event: StripeCardNumberElementChangeEvent
  ) => {
    setFieldValidity(prev => ({ ...prev, cardNumber: event.complete && !event.error }));
    if (!disabled) {
      onElementChange(event);
    }
  };

  const handleCardExpiryChange = (
    event: StripeCardExpiryElementChangeEvent
  ) => {
    setFieldValidity(prev => ({ ...prev, expiry: event.complete && !event.error }));
    if (!disabled) {
      onElementChange(event);
    }
  };

  const handleCardCvcChange = (event: StripeCardCvcElementChangeEvent) => {
    setFieldValidity(prev => ({ ...prev, cvc: event.complete && !event.error }));
    if (!disabled) {
      onElementChange(event);
    }
  };

  const disabledStyle = disabled
    ? { opacity: 0.5, pointerEvents: "none" as const }
    : {};

  return (
    <div
      style={{
        ...disabledStyle,
      }}
    >
      <CardNumberElement
        className="p-3 h-[42px] md:p-2 md:h-[38px] landscape:p-2 landscape:h-[38px] mb-[-1px] border rounded-[6px_6px_0px_0px] border-[#ECECEC]"
        onChange={handleCardNumberChange}
        options={elementOptions("0000 0000 0000 0000", "cc-number")}
        onFocus={onAnyFieldFocus}
        onBlur={() => handleFieldBlur('card number', fieldValidity.cardNumber)}
      />
      <div className="flex">
        <CardExpiryElement
          className="p-3 h-[42px] md:p-2 md:h-[38px] landscape:p-2 landscape:h-[38px] border border-[#ECECEC] w-1/2"
          onChange={handleCardExpiryChange}
          options={elementOptions("MM/YY", "cc-exp")}
          onFocus={onAnyFieldFocus}
          onBlur={() => handleFieldBlur('expiry date', fieldValidity.expiry)}
        />
        <CardCvcElement
          className="p-3 h-[42px] md:p-2 md:h-[38px] landscape:p-2 landscape:h-[38px] border-[1px_1px_1px_0px] border-[#ECECEC] w-1/2"
          onChange={handleCardCvcChange}
          options={elementOptions("CVC", "cc-csc")}
          onFocus={onAnyFieldFocus}
          onBlur={() => handleFieldBlur('cvc', fieldValidity.cvc)}
        />
      </div>
    </div>
  );
};

export default CreditCardCardForm;
